import { type ChangeEvent, type ComponentProps, useState } from 'react';
import { Dialog, DialogTitle, DialogActions } from '@mui/material';
import { account_controller } from '../../controllers/account_controller';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  companyAuthSettingsAtom,
  firstTimeLoginAtom,
} from '../../models/atoms/account_atoms';
import { useTranslation } from '../../contexts/TranslationContext';
import { MHMDialog } from './MHM/MHMDialog';
import { MHMPasswordField } from './MHM/MHMPasswordField';
import { MHMLabelledCheckbox } from './MHM/MHMLabelledCheckbox';
import { ErrorText, MHMTypography, DisclaimerText } from './MHM/MHMTypography';
import { MHMHyperlink } from './MHM/MHMHyperlink';
import { auth_controller } from '../../controllers/auth_controller';
import { MHMButton } from './MHM/MHMButton';
import { usePasswordFields } from '../../hooks/usePasswordFields';

const PolicyLinks: React.FC = () => {
  const { t } = useTranslation();
  return (
    <MHMTypography>
      {t('agree_to_tou_prefix')}&nbsp;
      <MHMHyperlink
        href="https://termsofuse.manholemetrics.com"
        target="_blank"
      >
        {t('Terms Of Use')}
      </MHMHyperlink>
      &nbsp;{t('agree_to_tou_and')}&nbsp;
      <MHMHyperlink
        href="https://privacyandcookies.manholemetrics.com"
        target="_blank"
      >
        {t('Privacy Policy')}
      </MHMHyperlink>
      &nbsp;{t('agree_to_tou_postfix')}
    </MHMTypography>
  );
};

function LimitedWidthErrorText(props: ComponentProps<typeof ErrorText>) {
  // minWidth + maxWidth trick to prevent error text from expanding its parents horizontally.
  // Source: https://stackoverflow.com/a/73654975
  return (
    <ErrorText
      sx={{ minWidth: '100%', maxWidth: 0, display: 'block' }}
      component="span"
      {...props}
    />
  );
}

const FirstLoginModal: React.FC = () => {
  const [firstTimeLogin, setFirstTimeLogin] =
    useRecoilState(firstTimeLoginAtom);
  const [error, setError] = useState('');
  const { t } = useTranslation();
  const [readManual, setReadManual] = useState(false);
  const [showReadManualMessage, setShowReadManualMessage] = useState(false);
  const [emailAlerts, setEmailAlerts] = useState(false);
  const [textAlerts, setTextAlerts] = useState(false);
  const companyAuthSettings = useRecoilValue(companyAuthSettingsAtom);
  const {
    curPassword,
    newPassword,
    newPasswordChanged,
    rePassword,
    rePasswordChanged,
    passwordValidationError,
    matchPasswordsError,
    isPasswordValid,
    curPasswordError,
    setCurPasswordError,
    handleCurPasswordChange,
    handleNewPasswordChange,
    handleRepeatPasswordChange,
    resetPasswordFields,
  } = usePasswordFields();

  const isButtonDisabled =
    (companyAuthSettings?.auth_type === 'password' && !isPasswordValid) ||
    !readManual;

  function toggleReadManual({
    target: { checked },
  }: ChangeEvent<HTMLInputElement>) {
    setReadManual(checked);
    setShowReadManualMessage(!checked);
  }

  function toggleEmailAlerts(e: ChangeEvent<HTMLInputElement>) {
    setEmailAlerts(e.target.checked);
  }

  function toggleTextAlerts(e: ChangeEvent<HTMLInputElement>) {
    setTextAlerts(e.target.checked);
  }

  function checkReadManual() {
    if (readManual) {
      return;
    }

    setShowReadManualMessage(true);
  }

  async function handleSubmit() {
    try {
      if (companyAuthSettings?.auth_type === 'password') {
        await account_controller.change_user_password(newPassword, curPassword);
      }
      account_controller.modify_user_account({
        email_alerts: emailAlerts,
        text_alerts: textAlerts,
      });
      setFirstTimeLogin(false);
      resetPasswordFields();
      setError('');
      localStorage.setItem('firstTimeLogin', 'false');
    } catch (e) {
      console.error('Validation failed:', e);
      const { detail } = e as { detail?: string };

      if (!detail) {
        throw e;
      }

      if (detail === 'Account Not Found') {
        auth_controller.logout();
      } else if (detail === 'Incorrect Password') {
        setCurPasswordError('Incorrect Password: Check Password in Email');
      } else {
        setError(detail);
      }
    }
  }

  return (
    <Dialog open={firstTimeLogin}>
      {companyAuthSettings?.auth_type === 'password' && (
        <DialogTitle>{t('Change Password')}</DialogTitle>
      )}
      <MHMDialog.Content>
        {companyAuthSettings?.auth_type === 'password' && (
          <>
            <MHMPasswordField
              autoFocus
              required
              margin="dense"
              label={t('Old Password')}
              value={curPassword}
              fullWidth
              onChange={handleCurPasswordChange}
              error={Boolean(curPasswordError)}
              helperText={
                <LimitedWidthErrorText>
                  {t(curPasswordError)}
                </LimitedWidthErrorText>
              }
            />
            <MHMPasswordField
              required
              margin="dense"
              label={t('New Password')}
              value={newPassword}
              fullWidth
              onChange={handleNewPasswordChange}
              error={Boolean(newPasswordChanged && passwordValidationError)}
              helperText={
                newPasswordChanged ? (
                  <LimitedWidthErrorText>
                    {t(passwordValidationError)}
                  </LimitedWidthErrorText>
                ) : null
              }
            />
            <MHMPasswordField
              required
              margin="dense"
              id="confirmPassword"
              name="confirmPassword"
              label={t('Repeat New Password')}
              value={rePassword}
              fullWidth
              onChange={handleRepeatPasswordChange}
              error={Boolean(rePasswordChanged && matchPasswordsError)}
              helperText={
                rePasswordChanged ? (
                  <LimitedWidthErrorText>
                    {t(matchPasswordsError)}
                  </LimitedWidthErrorText>
                ) : null
              }
            />
            <MHMHyperlink
              href="https://device-service.manhole-metrics.com/redirect-to-password-policy"
              target="_blank"
            >
              {t('View our Password Policy')}
            </MHMHyperlink>
          </>
        )}

        <div>
          <MHMTypography medium sx={{ paddingTop: '0.5rem' }}>
            {t('Subscribe to Alerts')}
          </MHMTypography>
          <DisclaimerText fontSize="small">
            {t('You can change these options on the Settings page')}
          </DisclaimerText>
        </div>
        <MHMLabelledCheckbox
          checkboxProps={{ checked: emailAlerts, onChange: toggleEmailAlerts }}
          label={<MHMTypography>{t('Email')}</MHMTypography>}
        />
        <MHMLabelledCheckbox
          checkboxProps={{ checked: textAlerts, onChange: toggleTextAlerts }}
          label={<MHMTypography>{t('SMS')}</MHMTypography>}
        />
        <LimitedWidthErrorText>{t(error)}</LimitedWidthErrorText>
        <MHMLabelledCheckbox
          checkboxProps={{ checked: readManual, onChange: toggleReadManual }}
          label={<PolicyLinks />}
        />
        {showReadManualMessage ? (
          <LimitedWidthErrorText>
            {t('You must agree to our Terms of Use and Privacy Policy!')}
          </LimitedWidthErrorText>
        ) : null}
      </MHMDialog.Content>
      <DialogActions>
        {/* put onClick handler here to handle click events when button is disabled */}
        <div onClick={checkReadManual}>
          <MHMButton disabled={isButtonDisabled} onClick={handleSubmit}>
            {t('Confirm')}
          </MHMButton>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default FirstLoginModal;
